import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout/Layout';
import { bannersAPI } from '../services/api';
import { eventAPI } from '../services/eventAPI';
import './BannerManagement.css';

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deletingBanners, setDeletingBanners] = useState(new Set());
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    image_url: '',
    image_type: 'upload',
    link_url: '',
    event_id: '',
    button_text: '',
    position: 0,
    is_active: true,
    target_page: 'home',
    background_color: '#1E40AF',
    text_color: '#FFFFFF',
    display_from: '',
    display_until: ''
  });
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [showCropEditor, setShowCropEditor] = useState(false);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [originalImageData, setOriginalImageData] = useState(null);

  useEffect(() => {
    fetchBanners();
    fetchStats();
    fetchEvents();
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvents = async () => {
    try {
      setLoadingEvents(true);
      const response = await eventAPI.getAllEvents({
        page: 1,
        limit: 100,
        status: 'published',
        sort_by: 'start_date',
        sort_order: 'ASC'
      });
      if (response.success && response.data && response.data.events) {
        setEvents(response.data.events);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    } finally {
      setLoadingEvents(false);
    }
  };

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await bannersAPI.getAll({
        page: currentPage,
        limit: 10,
        sort_by: 'position',
        sort_order: 'ASC'
      });
      
      if (response.success) {
        setBanners(response.data.banners || []);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (err) {
      setError('Failed to fetch banners: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await bannersAPI.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      image_url: '',
      image_type: 'upload',
      link_url: '',
      event_id: '',
      button_text: '',
      position: 0,
      is_active: true,
      target_page: 'home',
      background_color: '#1E40AF',
      text_color: '#FFFFFF',
      display_from: '',
      display_until: ''
    });
    setSelectedFile(null);
    setImagePreview(null);
    setImageDimensions({ width: 0, height: 0 });
    setShowImageEditor(false);
    setEditingBanner(null);
    setShowCreateForm(false);
  };

  // Banner dimensions: 1200x1200 (1:1 square)
  const TARGET_WIDTH = 1200;
  const TARGET_HEIGHT = 1200;
  const TARGET_ASPECT = TARGET_WIDTH / TARGET_HEIGHT; // 1.0 (1:1)

  // Initialize crop area when image is loaded
  const initializeCropArea = (imgWidth, imgHeight) => {
    const imgAspect = imgWidth / imgHeight;
    let cropWidth, cropHeight;
    
    if (imgAspect > TARGET_ASPECT) {
      // Image is wider than target ratio, crop width
      cropHeight = imgHeight;
      cropWidth = imgHeight * TARGET_ASPECT;
    } else {
      // Image is taller than target ratio, crop height
      cropWidth = imgWidth;
      cropHeight = imgWidth / TARGET_ASPECT;
    }
    
    // Center the crop area
    const x = (imgWidth - cropWidth) / 2;
    const y = (imgHeight - cropHeight) / 2;
    
    setCropArea({ x, y, width: cropWidth, height: cropHeight });
  };

  // Image adjustment functions
  const adjustImageToBannerSize = () => {
    if (!imagePreview) return;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = TARGET_WIDTH;
      canvas.height = TARGET_HEIGHT;
      
      // Calculate scaling to maintain aspect ratio while filling the target size
      const scaleX = TARGET_WIDTH / img.width;
      const scaleY = TARGET_HEIGHT / img.height;
      const scale = Math.max(scaleX, scaleY);
      
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
      
      // Center the image
      const x = (TARGET_WIDTH - scaledWidth) / 2;
      const y = (TARGET_HEIGHT - scaledHeight) / 2;
      
      // Draw the image
      ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
      
      // Convert to blob and update file
      canvas.toBlob((blob) => {
        const adjustedFile = new File([blob], selectedFile.name, { type: 'image/jpeg' });
        setSelectedFile(adjustedFile);
        
        // Update preview
        const newPreviewUrl = URL.createObjectURL(blob);
        setImagePreview(newPreviewUrl);
        setImageDimensions({ width: TARGET_WIDTH, height: TARGET_HEIGHT });
        
        setShowImageEditor(false);
        setShowCropEditor(false);
      }, 'image/jpeg', 0.9);
    };
    
    img.src = imagePreview;
  };

  const cropImageToBannerSize = () => {
    if (!imagePreview || !originalImageData) return;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = TARGET_WIDTH;
      canvas.height = TARGET_HEIGHT;
      
      // Use the selected crop area
      const { x, y, width, height } = cropArea;
      
      // Draw the cropped image
      ctx.drawImage(img, x, y, width, height, 0, 0, TARGET_WIDTH, TARGET_HEIGHT);
      
      // Convert to blob and update file
      canvas.toBlob((blob) => {
        const croppedFile = new File([blob], selectedFile.name, { type: 'image/jpeg' });
        setSelectedFile(croppedFile);
        
        // Update preview
        const newPreviewUrl = URL.createObjectURL(blob);
        setImagePreview(newPreviewUrl);
        setImageDimensions({ width: TARGET_WIDTH, height: TARGET_HEIGHT });
        
        setShowImageEditor(false);
        setShowCropEditor(false);
      }, 'image/jpeg', 0.9);
    };
    
    img.src = originalImageData;
  };

  // Crop editor mouse handlers
  const cropContainerRef = useRef(null);
  const [cropPreviewDimensions, setCropPreviewDimensions] = useState({ width: 0, height: 0 });

  const handleCropMouseDown = (e) => {
    if (!showCropEditor || !cropContainerRef.current) return;
    e.preventDefault();
    setIsDragging(true);
    // const rect = cropContainerRef.current.getBoundingClientRect();
    const img = cropContainerRef.current.querySelector('img');
    if (!img) return;
    
    const imgRect = img.getBoundingClientRect();
    const scaleX = imageDimensions.width / imgRect.width;
    const scaleY = imageDimensions.height / imgRect.height;
    
    const x = (e.clientX - imgRect.left) * scaleX;
    const y = (e.clientY - imgRect.top) * scaleY;
    
    // Check if click is within crop area
    if (x >= cropArea.x && x <= cropArea.x + cropArea.width &&
        y >= cropArea.y && y <= cropArea.y + cropArea.height) {
      setDragStart({ 
        x: e.clientX - imgRect.left, 
        y: e.clientY - imgRect.top,
        cropX: cropArea.x,
        cropY: cropArea.y
      });
    }
  };

  const handleCropMouseMove = (e) => {
    if (!isDragging || !showCropEditor || !cropContainerRef.current) return;
    
    const img = cropContainerRef.current.querySelector('img');
    if (!img) return;
    
    const imgRect = img.getBoundingClientRect();
    const scaleX = imageDimensions.width / imgRect.width;
    const scaleY = imageDimensions.height / imgRect.height;
    
    // const mouseX = (e.clientX - imgRect.left) * scaleX;
    // const mouseY = (e.clientY - imgRect.top) * scaleY;
    
    const deltaX = (e.clientX - imgRect.left - dragStart.x) * scaleX;
    const deltaY = (e.clientY - imgRect.top - dragStart.y) * scaleY;
    
    // Calculate new crop position
    let newX = dragStart.cropX + deltaX;
    let newY = dragStart.cropY + deltaY;
    
    // Constrain crop area within image bounds
    newX = Math.max(0, Math.min(newX, imageDimensions.width - cropArea.width));
    newY = Math.max(0, Math.min(newY, imageDimensions.height - cropArea.height));
    
    setCropArea(prev => ({ ...prev, x: newX, y: newY }));
  };

  const handleCropMouseUp = () => {
    setIsDragging(false);
  };

  // Update crop preview dimensions when image loads
  useEffect(() => {
    if (showCropEditor && cropContainerRef.current) {
      const img = cropContainerRef.current.querySelector('img');
      if (img) {
        img.onload = () => {
          setCropPreviewDimensions({ 
            width: img.offsetWidth, 
            height: img.offsetHeight 
          });
        };
      }
    }
  }, [showCropEditor, originalImageData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setFormData(prev => ({ ...prev, image_type: 'upload' }));
    
    if (file) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      
      // Store original image data for cropping
      const reader = new FileReader();
      reader.onload = (event) => {
        setOriginalImageData(event.target.result);
      };
      reader.readAsDataURL(file);
      
      // Get image dimensions
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        // Initialize crop area
        initializeCropArea(img.naturalWidth, img.naturalHeight);
      };
      img.src = previewUrl;
    } else {
      setImagePreview(null);
      setImageDimensions({ width: 0, height: 0 });
      setOriginalImageData(null);
      setShowCropEditor(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let response;
      
      if (selectedFile) {
        // Upload with image
        const formDataObj = new FormData();
        formDataObj.append('banner_image', selectedFile);
        
        Object.keys(formData).forEach(key => {
          if (formData[key] !== '') {
            formDataObj.append(key, formData[key]);
          }
        });
        
        if (editingBanner) {
          response = await bannersAPI.updateWithImage(editingBanner.id, formDataObj);
        } else {
          response = await bannersAPI.createWithImage(formDataObj);
        }
      } else {
        // JSON only
        if (editingBanner) {
          response = await bannersAPI.update(editingBanner.id, formData);
        } else {
          response = await bannersAPI.create(formData);
        }
      }
      
      if (response.success) {
        resetForm();
        fetchBanners();
        fetchStats();
        setError(null);
      }
    } catch (err) {
      console.error('Banner save error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error occurred';
      setError('Failed to save banner: ' + errorMessage);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      subtitle: banner.subtitle || '',
      image_url: banner.image_url || '',
      image_type: banner.image_type || 'url',
      link_url: banner.link_url || '',
      event_id: banner.event_id || '',
      button_text: banner.button_text || '',
      position: banner.position || 0,
      is_active: banner.is_active,
      target_page: banner.target_page || 'home',
      background_color: banner.background_color || '#1E40AF',
      text_color: banner.text_color || '#FFFFFF',
      display_from: banner.display_from ? banner.display_from.split('T')[0] : '',
      display_until: banner.display_until ? banner.display_until.split('T')[0] : ''
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (banner) => {
    // Prevent multiple simultaneous deletions
    if (deletingBanners.has(banner.id)) {
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete "${banner.title}"?`)) {
      try {
        // Mark banner as being deleted
        setDeletingBanners(prev => new Set([...prev, banner.id]));
        
        const response = await bannersAPI.delete(banner.id);
        if (response.success) {
          fetchBanners();
          fetchStats();
          setError(null);
        }
      } catch (err) {
        // If banner is already deleted (404), treat it as success
        if (err.response?.status === 404) {
          console.log('Banner already deleted, refreshing list...');
          fetchBanners();
          fetchStats();
          setError(null);
        } else {
          setError('Failed to delete banner: ' + (err.response?.data?.message || err.message));
        }
      } finally {
        // Remove banner from deleting state
        setDeletingBanners(prev => {
          const newSet = new Set(prev);
          newSet.delete(banner.id);
          return newSet;
        });
      }
    }
  };

  const handleToggleStatus = async (banner) => {
    try {
      const response = await bannersAPI.toggleStatus(banner.id);
      if (response.success) {
        fetchBanners();
        fetchStats();
        setError(null);
      }
    } catch (err) {
      console.error('Toggle status error:', err);
      setError('Failed to toggle banner status: ' + (err.response?.data?.message || err.message));
    }
  };

  if (loading) {
    return (
      <Layout title="Banner Management">
        <div className="loading">Loading banners...</div>
      </Layout>
    );
  }

  return (
    <Layout title="Banner Management">
      <div className="banner-management">
        
        {/* Stats Cards */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{stats.total_banners}</div>
              <div className="stat-label">Total Banners</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.active_banners}</div>
              <div className="stat-label">Active Banners</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.total_views}</div>
              <div className="stat-label">Total Views</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.total_clicks}</div>
              <div className="stat-label">Total Clicks</div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Action Bar */}
        <div className="action-bar">
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateForm(true)}
          >
            🏷️ Create New Banner
          </button>
          
          <button 
            className="btn btn-secondary"
            onClick={() => fetchBanners()}
          >
            🔄 Refresh
          </button>
        </div>

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div className="form-overlay">
            <div className="form-container">
              <h3>{editingBanner ? 'Edit Banner' : 'Create New Banner'}</h3>
              
              <div className="info-message">
                💡 <strong>Tip:</strong> Upload image files directly for best results. You can also use external image URLs if needed. Uploaded images will be automatically converted to accessible URLs.
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      maxLength={255}
                    />
                  </div>

                  <div className="form-group">
                    <label>Subtitle</label>
                    <textarea
                      name="subtitle"
                      value={formData.subtitle}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Image Type</label>
                    <select
                      name="image_type"
                      value={formData.image_type}
                      onChange={handleInputChange}
                    >
                      <option value="upload">Upload File</option>
                      <option value="url">External URL</option>
                    </select>
                  </div>

                  {formData.image_type === 'url' ? (
                    <div className="form-group">
                      <label>Image URL</label>
                      <input
                        type="url"
                        name="image_url"
                        value={formData.image_url}
                        onChange={handleInputChange}
                      />
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>Upload Image</label>
                      <div className="image-upload-section">
                        <div className="dimension-info">
                          <p><strong>Recommended Dimensions:</strong> 1200 × 900 pixels (4:3 ratio)</p>
                          <p><em>For best results, use images with this aspect ratio</em></p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                        
                        {imagePreview && (
                          <div className="image-preview-section">
                            <div className="current-dimensions">
                              <p><strong>Current Dimensions:</strong> {imageDimensions.width} × {imageDimensions.height} pixels</p>
                              {imageDimensions.width > 0 && imageDimensions.height > 0 && (
                                <p className={`dimension-status ${
                                  imageDimensions.width === TARGET_WIDTH && imageDimensions.height === TARGET_HEIGHT 
                                    ? 'perfect' 
                                    : Math.abs((imageDimensions.width / imageDimensions.height) - TARGET_ASPECT) < 0.1 
                                      ? 'good' 
                                      : 'needs-adjustment'
                                }`}>
                                  {imageDimensions.width === TARGET_WIDTH && imageDimensions.height === TARGET_HEIGHT 
                                    ? '✅ Perfect dimensions!' 
                                    : Math.abs((imageDimensions.width / imageDimensions.height) - TARGET_ASPECT) < 0.1 
                                      ? '✅ Good aspect ratio' 
                                      : '⚠️ Consider adjusting to 4:3 ratio'
                                  }
                                </p>
                              )}
                            </div>
                            
                            <div className="image-preview">
                              {showCropEditor && originalImageData ? (
                                <div 
                                  ref={cropContainerRef}
                                  className="crop-container"
                                  onMouseDown={handleCropMouseDown}
                                  onMouseMove={handleCropMouseMove}
                                  onMouseUp={handleCropMouseUp}
                                  onMouseLeave={handleCropMouseUp}
                                  style={{ 
                                    position: 'relative', 
                                    display: 'inline-block', 
                                    cursor: isDragging ? 'grabbing' : 'grab',
                                    userSelect: 'none'
                                  }}
                                >
                                  <img 
                                    src={originalImageData} 
                                    alt="Crop Preview" 
                                    style={{ maxWidth: '100%', maxHeight: '400px', display: 'block' }}
                                    draggable={false}
                                  />
                                  {cropPreviewDimensions.width > 0 && (
                                    <>
                                      <div 
                                        className="crop-overlay"
                                        style={{
                                          position: 'absolute',
                                          left: `${(cropArea.x / imageDimensions.width) * (cropPreviewDimensions.width || 1)}px`,
                                          top: `${(cropArea.y / imageDimensions.height) * (cropPreviewDimensions.height || 1)}px`,
                                          width: `${(cropArea.width / imageDimensions.width) * (cropPreviewDimensions.width || 1)}px`,
                                          height: `${(cropArea.height / imageDimensions.height) * (cropPreviewDimensions.height || 1)}px`,
                                          border: '3px solid #3b82f6',
                                          backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                          boxSizing: 'border-box',
                                          pointerEvents: 'none',
                                          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)'
                                        }}
                                      >
                                        <div style={{
                                          position: 'absolute',
                                          top: '-30px',
                                          left: 0,
                                          background: '#3b82f6',
                                          color: 'white',
                                          padding: '4px 10px',
                                          fontSize: '12px',
                                          borderRadius: '4px',
                                          whiteSpace: 'nowrap',
                                          fontWeight: 'bold'
                                        }}>
                                          🖱️ Drag to adjust crop area
                                        </div>
                                        <div style={{
                                          position: 'absolute',
                                          bottom: '-25px',
                                          left: '50%',
                                          transform: 'translateX(-50%)',
                                          background: 'rgba(0, 0, 0, 0.7)',
                                          color: 'white',
                                          padding: '2px 8px',
                                          fontSize: '11px',
                                          borderRadius: '4px'
                                        }}>
                                          {Math.round(cropArea.width)} × {Math.round(cropArea.height)}px
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <img src={imagePreview} alt="Preview" />
                              )}
                            </div>
                            
                            <div className="image-adjustment-buttons">
                              <button 
                                type="button" 
                                className="btn btn-secondary"
                                onClick={() => setShowImageEditor(!showImageEditor)}
                              >
                                🖼️ Adjust Image
                              </button>
                              
                              {showImageEditor && (
                                <div className="image-editor-options">
                                  <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={adjustImageToBannerSize}
                                  >
                                    📏 Fit to Banner Size (1200×900)
                                  </button>
                                  {originalImageData && (
                                    <button 
                                      type="button" 
                                      className="btn btn-primary"
                                      onClick={() => {
                                        setShowCropEditor(!showCropEditor);
                                        if (!showCropEditor) {
                                          // Reset to original image when opening crop editor
                                          const img = new Image();
                                          img.onload = () => {
                                            setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                                            initializeCropArea(img.naturalWidth, img.naturalHeight);
                                          };
                                          img.src = originalImageData;
                                        }
                                      }}
                                    >
                                      {showCropEditor ? '✂️ Close Crop Editor' : '✂️ Select Crop Area (4:3)'}
                                    </button>
                                  )}
                                  {showCropEditor && (
                                    <button 
                                      type="button" 
                                      className="btn btn-success"
                                      onClick={cropImageToBannerSize}
                                    >
                                      ✅ Apply Crop
                                    </button>
                                  )}
                                  <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => {
                                      setShowImageEditor(false);
                                      setShowCropEditor(false);
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Link to Event</label>
                    <select
                      name="event_id"
                      value={formData.event_id}
                      onChange={handleInputChange}
                    >
                      <option value="">-- Select an Event (Optional) --</option>
                      {loadingEvents ? (
                        <option disabled>Loading events...</option>
                      ) : events.length > 0 ? (
                        events.map(event => (
                          <option key={event.id} value={event.id}>
                            {event.title} {event.start_date ? `(${new Date(event.start_date).toLocaleDateString()})` : ''}
                          </option>
                        ))
                      ) : (
                        <option disabled>No published events available</option>
                      )}
                    </select>
                    <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                      💡 If an event is selected, clicking the banner will navigate to the event page. Leave empty to use Link URL below for external ads.
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Link URL (External or Internal Route)</label>
                    <input
                      type="url"
                      name="link_url"
                      value={formData.link_url}
                      onChange={handleInputChange}
                      placeholder="https://example.com or /attractions"
                      disabled={!!formData.event_id}
                    />
                    <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                      {formData.event_id 
                        ? '⚠️ Disabled when Event is selected (Event link takes priority)' 
                        : 'Use for external ads (e.g., Celcom) or internal app routes. Ignored if Event is selected above.'}
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Button Text</label>
                    <input
                      type="text"
                      name="button_text"
                      value={formData.button_text}
                      onChange={handleInputChange}
                      maxLength={100}
                    />
                  </div>

                  <div className="form-group">
                    <label>Position</label>
                    <input
                      type="number"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label>Target Page</label>
                    <select
                      name="target_page"
                      value={formData.target_page}
                      onChange={handleInputChange}
                    >
                      <option value="home">Home</option>
                      <option value="attractions">Attractions</option>
                      <option value="favorites">Favorites</option>
                      <option value="about">About</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Background Color</label>
                    <input
                      type="color"
                      name="background_color"
                      value={formData.background_color}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Text Color</label>
                    <input
                      type="color"
                      name="text_color"
                      value={formData.text_color}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Display From</label>
                    <input
                      type="date"
                      name="display_from"
                      value={formData.display_from}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Display Until</label>
                    <input
                      type="date"
                      name="display_until"
                      value={formData.display_until}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group full-width">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                      Active
                    </label>
                  </div>
                  
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingBanner ? 'Update Banner' : 'Create Banner'}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Banners Table */}
        <div className="banners-table">
          <table>
            <thead>
              <tr>
                <th>Preview</th>
                <th>Title</th>
                <th>Target Page</th>
                <th>Position</th>
                <th>Status</th>
                <th>Views/Clicks</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {banners.map(banner => (
                <tr key={banner.id}>
                  <td>
                    {banner.image_url && (
                      <img 
                        src={banner.image_url} 
                        alt={banner.title}
                        className="banner-preview"
                      />
                    )}
                  </td>
                  <td>
                    <div className="banner-info">
                      <strong>{banner.title}</strong>
                      {banner.subtitle && <div className="subtitle">{banner.subtitle}</div>}
                    </div>
                  </td>
                  <td>{banner.target_page}</td>
                  <td>{banner.position}</td>
                  <td>
                    <span className={`status ${banner.is_active ? 'active' : 'inactive'}`}>
                      {banner.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="stats">
                      <div>👀 {banner.view_count}</div>
                      <div>👆 {banner.click_count}</div>
                    </div>
                  </td>
                  <td>
                    <div className="actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(banner)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className={`btn btn-sm ${banner.is_active ? 'btn-warning' : 'btn-success'}`}
                        onClick={() => handleToggleStatus(banner)}
                        title={banner.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {banner.is_active ? '⏸️' : '▶️'}
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(banner)}
                        disabled={deletingBanners.has(banner.id)}
                        title={deletingBanners.has(banner.id) ? "Deleting..." : "Delete"}
                      >
                        {deletingBanners.has(banner.id) ? '⏳' : '🗑️'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {banners.length === 0 && !loading && (
            <div className="empty-state">
              <p>No banners found. Create your first banner to get started!</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BannerManagement;
